import Storage from './storage.js';
import db from './db.js';
import grade from './resolutionGrade.js';

const imageHelper = {


    /**
     * Get the current active image (either server URL or local blob).
     * @returns {Promise<{ source: "server" | "local", image: Object, url: string } | null>}
     */
    async getActiveImage() {
        const id = Storage.get('activeImageID');
        if (!id) return null;

        return this.getImage(id);
    },

    /**
    * Get the current active image resolution.
    * @returns { Promise<{ width: number, height: number } | null>}
    */
    async getActiveImageResolution() {
        const image = await this.getActiveImage();
        if (!image) return null;

        return image.image?.imageResolution || null;
    },

    getImageOrientation(resolution) {
        if (typeof resolution === 'object' && 'width' in resolution && 'height' in resolution) {
            return (resolution.width > resolution.height) ? 'landscape' : 'portrait';
        }
        return null;
    },

    /**
     * Gets the resolution of an image.
     * @param {*} fileOrBlobOrUrl - The image file, Blob, or URL.
     * @returns {Promise<{ width: number, height: number }>} - A promise that resolves to the image resolution.
     */
    async getImageResolution(fileOrBlobOrUrl) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            if (typeof fileOrBlobOrUrl === 'string') {
                img.src = fileOrBlobOrUrl;
            } else {
                img.src = URL.createObjectURL(fileOrBlobOrUrl);
            }

            img.onload = () => {
                const resolution = { width: img.width, height: img.height };
                if (typeof fileOrBlobOrUrl !== 'string') {
                    URL.revokeObjectURL(img.src);
                }
                resolve(resolution);
            };

            img.onerror = (e) => {
                resolve({ width: 0, height: 0 });
            };
        });
    },

    /**
     * Get image (either server URL or local blob).
     * @param {string} id - The ID of the image to retrieve.
     * @returns {Promise<{ source: "server" | "local", image: Object, url: string }>}
     */
    async getImage(id) {

        const image = await db.getImageData(id); //
        if (!image) return null; //

        let url = null;
        let source = 'local'; // Sätt standard

        if (image.uploadStatus === 'uploaded' && image.serverUrl) { //
            // Fall 1: Redan uppladdad, använd server-URL
            url = image.serverUrl;
            source = 'server';
        } else if (image.blobUrl) {
            // Fall 2: JPG/PNG/Safari-HEIC, använd blob-URL
            url = image.blobUrl;
            source = 'local';
        }
        // Fall 3: Chrome-HEIC. 'url' förblir 'null'.

        // *** NYCKELÄNDRINGEN: ***
        // Returnera objektet oavsett, så 'loadActiveImage' kan
        // inspektera 'uploadStatus' från 'image.image'-objektet.
        return {
            source: source,
            image: image, // Hela fileData-objektet från db.js
            url: url      // Denna är 'null' för Chrome-HEIC
        };
    },

    /**
     * Get the current grade based on the active image resolution, size, presentation, orientation, and crop box.
     * @returns {Promise<number>} - Returns the current grade based on the active image resolution, size, presentation, orientation, and crop box.
     */
    async activeGrade() {
        return new Promise(async (resolve) => {
            const presentation = await Storage.get('presentation');
            const orientation = await Storage.get('orientation');
            const size = await Storage.get('size');
            const cropBox = await Storage.get('cropBox');
            const paper = await Storage.get('paper');
            const imageResolution = await this.getActiveImageResolution();
            grade.get(
                imageResolution,
                size,
                presentation,
                orientation,
                cropBox,
                paper
            ).then((newGrade) => {
                resolve(newGrade);
            });
        });
    }
};

export default imageHelper;